import api from "../api";
import { appendFileToFormData } from "../form-data";
import { EditVehicleData, VehicleData } from "../validation/vehicle";

export type VehicleCategory = "under_3.5t" | "3.5-7.5t" | "over_7.5t";

export interface Document {
  id: number;
  type: string;
  url: string;
  mime_type: string;
  size: number;
  is_verified: boolean;
  verified_at?: string;
  rejection_reason?: string;
  created_at: string;
}

export interface Vehicle {
  id: number;
  registration_number: string;
  vin?: string;
  vehicle_type: VehicleCategory;
  make?: string;
  model?: string;
  year?: number;
  mileage?: string;
  is_verified: boolean;
  is_available: boolean;
  driver?: any;
  documents?: Document[];
  equipment?: string[];
  services_enabled?: string[];
  created_at: string;
}

class VehicleService {
  async getVehicles(): Promise<Vehicle[]> {
    const response = await api.get("/vehicles");
    return response.data.data;
  }

  async getVehicle(id: number): Promise<Vehicle> {
    const response = await api.get(`/vehicles/${id}`);
    return response.data.data;
  }

  async createVehicle(data: VehicleData): Promise<Vehicle> {
    const formData = new FormData();

    // Add basic fields
    Object.entries(data).forEach(([key, value]) => {
      if (
        value !== undefined &&
        !["vehicle_photo", "vehicle_insurance", "equipment", "services_enabled"].includes(key)
      ) {
        formData.append(key, String(value));
      }
    });

    // Handle equipment array
    if (data.equipment && Array.isArray(data.equipment)) {
      data.equipment.forEach((item) => {
        formData.append(`equipment[]`, item);
      });
    }

    // Handle services array
    if (data.services_enabled && Array.isArray(data.services_enabled)) {
      data.services_enabled.forEach((item) => {
        formData.append(`services_enabled[]`, item);
      });
    }

    // Add files
    if (data.vehicle_photo) {
      await appendFileToFormData(formData, "vehicle_photo", data.vehicle_photo);
    }

    if (data.vehicle_insurance) {
      await appendFileToFormData(formData, "vehicle_insurance", data.vehicle_insurance);
    }

    const response = await api.post("/vehicles", formData);
    return response.data.data;
  }

  async updateVehicle(
    id: number,
    data: EditVehicleData,
  ): Promise<Vehicle> {
    const response = await api.put(`/vehicles/${id}`, data);
    return response.data.data;
  }

  async deleteVehicle(id: number): Promise<void> {
    await api.delete(`/vehicles/${id}`);
  }

  async updateAvailability(
    id: number,
    is_available: boolean,
  ): Promise<Vehicle> {
    const response = await api.put(`/vehicles/${id}/availability`, {
      is_available,
    });
    return response.data.data;
  }

  async uploadDocument(
    vehicleId: number,
    type: string,
    file: any,
  ): Promise<Document> {
    const formData = new FormData();
    formData.append("vehicle_id", String(vehicleId));
    formData.append("type", type);
    await appendFileToFormData(formData, "file", {
      uri: file.uri,
      type: file.mimeType || file.type || "image/jpeg",
      name: file.fileName || file.name || `${type}.jpg`,
    });

    const response = await api.post("/documents", formData);
    return response.data.data;
  }
}

export default new VehicleService();
